import React, { JSXElementConstructor, useState } from "react";
import { useSelector } from "react-redux";
import { Switch } from "react-router-dom";
import { Toaster } from "sonner";

import {
  FeatureGate,
  getBooleanFeatureGateEnabled,
  getFeatureGates,
  getLocaleWithScript,
  PageBanner,
  RouteConfig,
} from "@gamedev-web/gamedev-sdk-core";

import { IconButton } from "../components";
import { Route } from "../components/Route";
import { NavLinksPane } from "../components/TestPanels/NavLinksPane";
import { TestPanelProps } from "../components/TestPanels/TestPanelContainer";
import { ToolsPane } from "../components/TestPanels/ToolsPane";
import { UhfFooter } from "../components/UHF/UhfFooter";
import { UhfHeader } from "../components/UHF/UhfHeader";
import { GlyphIcon } from "../helpers";
import { RootState } from "../redux/reducers/rootReducer";
import { evaluateRouteConstraint, getRoutes } from "../routes";
import * as styles from "./Shell.module.css";

type TestPanelConfig = Readonly<{
  key: string;
  glyph: GlyphIcon;
  component: JSXElementConstructor<TestPanelProps>;
  buttonTitle: string;
}>;

const getTestPanelsConfig = (): TestPanelConfig[] => [
  {
    key: "NavLinks",
    glyph: GlyphIcon.LeftNavButton,
    component: NavLinksPane,
    buttonTitle: "Navigation Links",
  },
  {
    key: "Tools",
    glyph: GlyphIcon.Settings,
    component: ToolsPane,
    buttonTitle: "Tools",
  },
];

const useTestPanels = () => {
  const [panelsState, setPanelsState] = useState<Dictionary<boolean>>({});

  const isEnableTestNav = useSelector((state: RootState) =>
    getBooleanFeatureGateEnabled(state, FeatureGate.EnableTestNav)
  );

  const isEnableTestTools = useSelector((state: RootState) =>
    getBooleanFeatureGateEnabled(state, FeatureGate.EnableTestTools)
  );

  const setIsPanelVisible = (key: string, isVisible: boolean) => {
    setPanelsState({
      ...panelsState,
      [key]: isVisible,
    });
  };

  const testPanelConfig = getTestPanelsConfig();

  return (
    (isEnableTestNav || isEnableTestTools) && (
      <div>
        <div id="test-panel-trigger-container" className={styles.testPanelTriggerContainer}>
          {testPanelConfig.map(config => (
            <IconButton
              aria-label={config.buttonTitle}
              iconName={config.glyph}
              key={config.key}
              onClick={() => setIsPanelVisible(config.key, true)}
              title={config.buttonTitle}
            />
          ))}
        </div>
        {testPanelConfig.map(config => (
          <config.component
            key={config.key}
            isOpen={!!panelsState[config.key]}
            onRequestClose={() => setIsPanelVisible(config.key, false)}
          />
        ))}
      </div>
    )
  );
};

export const Shell = () => {
  const featureGates = useSelector(getFeatureGates);
  const testPanels = useTestPanels();
  const locale = useSelector(getLocaleWithScript);
  return (
    <>
      <PageBanner />
      <Toaster />
      <UhfHeader />
      <div role="main" className={styles.shellContainer} id={"PageContent"} tabIndex={-1}>
        <Switch>
          {getRoutes().map(
            ({ key, constraint, testConfig: _3, ...routeProps }: RouteConfig<RootState>) => {
              if (evaluateRouteConstraint(constraint, featureGates, locale)) {
                return <Route key={key} {...routeProps} />;
              } else {
                return undefined;
              }
            }
          )}
        </Switch>
      </div>
      <UhfFooter />
      {testPanels}
    </>
  );
};
